import { JSONModel, toJSONContext } from '../../models/jsonModel';
import Image, { ImageJSON } from '@/store/models/image';

export enum BannerStandImageStatus {
  Inactive = 0,
  Active = 1,
}

export interface BannerStandImageJSON {
  id: number;
  product_id: number;
  language_code: string;
  product?: {
    id: number;
    title: string;
  };
  images?: (ImageJSON | null)[];
  // For sending to server
  image1?: ImageJSON;
  image2?: ImageJSON;
  image3?: ImageJSON;
  status: BannerStandImageStatus;
  created_at: string;
  updated_at: string;
}

export default class BannerStandImage extends JSONModel<BannerStandImageJSON> {
  id: number = 0;
  productId: number = 0;
  languageCode: string = '';
  product?: { id: number; title: string };
  image1?: Image;
  image2?: Image;
  image3?: Image;
  images: (Image | undefined)[] = [];
  status: BannerStandImageStatus = BannerStandImageStatus.Active;
  created_at: string = '';
  updated_at: string = '';

  public fromJSON(json: BannerStandImageJSON): void {
    this.id = Number(json.id);
    
    if (json.language_code) {
      this.languageCode = String(json.language_code);
    }
    
    if (json.product) {
      this.product = {
        id: json.product.id,
        title: json.product.title,
      };
      this.productId = json.product.id;
    } else {
      this.productId = Number(json.product_id || 0);
    }
    
    // Parse images from API response (images array) or from clone (image1, image2, image3)
    if (json.images && Array.isArray(json.images)) {
      // From API response
      if (json.images[0]) {
        this.image1 = Image.fromJSON(json.images[0]);
      }
      if (json.images[1]) {
        this.image2 = Image.fromJSON(json.images[1]);
      }
      if (json.images[2]) {
        this.image3 = Image.fromJSON(json.images[2]);
      }
    } else {
      // From clone or direct assignment
      if (json.image1) {
        this.image1 = Image.fromJSON(json.image1);
      }
      if (json.image2) {
        this.image2 = Image.fromJSON(json.image2);
      }
      if (json.image3) {
        this.image3 = Image.fromJSON(json.image3);
      }
    }
    
    // Populate images array for easy iteration
    this.images = [this.image1, this.image2, this.image3].filter(img => img !== undefined);

    this.status = Number(json.status) === 0 ? BannerStandImageStatus.Inactive : BannerStandImageStatus.Active;
    this.created_at = String(json.created_at || '');
    this.updated_at = String(json.updated_at || '');
  }

  public toJSON(context: toJSONContext = 'server'): BannerStandImageJSON {
    // Send as separate image1, image2, image3 for the upload mechanism
    return {
      id: this.id,
      product_id: this.productId,
      language_code: this.languageCode,
      product: this.product,
      image1: this.image1 ? (this.image1 instanceof Image ? this.image1.toJSON(context) : this.image1) : undefined,
      image2: this.image2 ? (this.image2 instanceof Image ? this.image2.toJSON(context) : this.image2) : undefined,
      image3: this.image3 ? (this.image3 instanceof Image ? this.image3.toJSON(context) : this.image3) : undefined,
      status: this.status,
      created_at: this.created_at,
      updated_at: this.updated_at,
    };
  }
}
